import React, { useId } from 'react';

import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import { BitbucketIcon } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';

import { getDevDocsSourceProviderConfigFromValue } from './source-resolution/devDocsSourceProviders.ts';

export const DevDocsGithubIcon = ({
	size,
	color = token('color.icon'),
}: {
	size: number;
	color?: string;
}): React.JSX.Element => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
		focusable="false"
	>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M11.9778 0C5.3635 0 0 5.45261 0 12.179C0 17.5601 3.432 22.1253 8.19114 23.7357C8.78975 23.8484 9.00952 23.4715 9.00952 23.1498C9.00952 22.8594 8.99841 21.9 8.99326 20.8823C5.66103 21.6191 4.95789 19.4454 4.95789 19.4454C4.41303 18.0376 3.62796 17.6633 3.62796 17.6633C2.54122 16.9074 3.70988 16.923 3.70988 16.923C4.91266 17.0089 5.54598 18.178 5.54598 18.178C6.61428 20.0399 8.34803 19.5016 9.03154 19.1904C9.13904 18.4033 9.44945 17.866 9.792 17.5619C7.13157 17.2539 4.33487 16.2096 4.33487 11.5429C4.33487 10.2133 4.80278 9.12682 5.56899 8.27391C5.44463 7.96716 5.03464 6.72844 5.68502 5.05087C5.68502 5.05087 6.69084 4.72354 8.97976 6.29927C9.9352 6.02942 10.9599 5.89409 11.9778 5.88945C12.9957 5.89409 14.0212 6.02942 14.9784 6.29927C17.2645 4.72354 18.269 5.05087 18.269 5.05087C18.9209 6.72844 18.5107 7.96716 18.3864 8.27391C19.1544 9.12682 19.6191 10.2133 19.6191 11.5429C19.6191 16.2207 16.8171 17.2507 14.1499 17.5522C14.5795 17.9301 14.9623 18.6713 14.9623 19.8076C14.9623 21.4371 14.9484 22.7487 14.9484 23.1498C14.9484 23.4739 15.164 23.8537 15.7712 23.7341C20.5277 22.1219 23.9554 17.5582 23.9554 12.179C23.9554 5.45261 18.5927 0 11.9778 0Z"
			fill={color}
		/>
	</svg>
);

export const DevDocsScmProviderIcon = ({
	provider,
	color,
}: {
	provider?: string | null;
	color?: string;
}): React.JSX.Element => {
	const providerKey = getDevDocsSourceProviderConfigFromValue(provider)?.provider;

	switch (providerKey) {
		case 'github':
			return <DevDocsGithubIcon size={16} color={color} />;
		case 'bitbucket':
			return <BitbucketIcon appearance="brand" label="" size="xsmall" />;
		default:
			return <AngleBracketsIcon label="" />;
	}
};

export const DevDocsScmProviderSourceIcon = ({
	sourceUrl,
}: {
	sourceUrl: string;
}): React.JSX.Element => <DevDocsScmProviderIcon provider={sourceUrl} />;

export const DevDocsScmProviderButtonIcons = (): React.JSX.Element => {
	const id = useId().replace(/:/g, '');
	const githubMaskId = `devDocsScmProviderButtonIconsGithubMask-${id}`;
	const clipId = `devDocsScmProviderButtonIconsClip0-${id}`;

	return (
		<svg
			width="40"
			height="23"
			viewBox="0 0 40 23"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			focusable="false"
		>
			<g mask={`url(#${githubMaskId})`}>
				<rect y="1.5" width="20" height="20" rx="5" fill="white" />
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M10.0001 5.5C6.68675 5.5 4 8.25424 4 11.6519C4 14.37 5.7192 16.676 8.1032 17.4894C8.40306 17.5464 8.51315 17.356 8.51315 17.1935C8.51315 17.0468 8.50759 16.5622 8.50501 16.0481C6.83579 16.4203 6.48356 15.3223 6.48356 15.3223C6.21062 14.6112 5.81736 14.4221 5.81736 14.4221C5.27298 14.0403 5.8584 14.0482 5.8584 14.0482C6.4609 14.0916 6.77816 14.6821 6.77816 14.6821C7.3133 15.6226 8.18179 15.3507 8.52418 15.1935C8.57803 14.7959 8.73353 14.5245 8.90512 14.3709C7.57243 14.2153 6.17147 13.6878 6.17147 11.3306C6.17147 10.6589 6.40586 10.1102 6.78968 9.67934C6.72739 9.52439 6.52201 8.89869 6.84781 8.05131C6.84781 8.05131 7.35165 7.88597 8.49825 8.6819C8.97686 8.54559 9.49014 8.47724 10.0001 8.47489C10.51 8.47724 11.0236 8.54559 11.5031 8.6819C12.6483 7.88597 13.1515 8.05131 13.1515 8.05131C13.4781 8.89869 13.2726 9.52439 13.2103 9.67934C13.595 10.1102 13.8278 10.6589 13.8278 11.3306C13.8278 13.6934 12.4242 14.2137 11.0881 14.366C11.3033 14.5569 11.4951 14.9313 11.4951 15.5052C11.4951 16.3284 11.4881 16.9909 11.4881 17.1935C11.4881 17.3572 11.5961 17.549 11.9003 17.4886C14.283 16.6742 16 14.3691 16 11.6519C16 8.25424 13.3137 5.5 10.0001 5.5Z"
					fill="#161514"
				/>
			</g>
			<g clipPath={`url(#${clipId})`}>
				<path
					d="M18 6.5C18 3.73858 20.2386 1.5 23 1.5L33 1.5C35.7614 1.5 38 3.73858 38 6.5V16.5C38 19.2614 35.7614 21.5 33 21.5H23C20.2386 21.5 18 19.2614 18 16.5V6.5Z"
					fill="#94C748"
				/>
				<path
					d="M32.8689 11.3016L32.0471 16.3216C31.9935 16.6253 31.7791 16.8039 31.4754 16.8039H24.5081C24.2044 16.8039 23.9901 16.6253 23.9365 16.3216L22.4894 7.37131C22.4358 7.06761 22.5966 6.87109 22.8824 6.87109H33.1011C33.3869 6.87109 33.5477 7.06761 33.4941 7.37131L33.1011 9.72946C33.0475 10.0689 32.851 10.2118 32.5294 10.2118H26.3839C26.2946 10.2118 26.241 10.2654 26.2589 10.3726L26.7412 13.3381C26.7591 13.4096 26.8127 13.4632 26.8842 13.4632H29.0994C29.1708 13.4632 29.2244 13.4096 29.2423 13.3381L29.5817 11.1944C29.6175 10.9264 29.7961 10.8192 30.0462 10.8192H32.458C32.8153 10.8192 32.9224 10.9979 32.8689 11.3016Z"
					fill="#101214"
				/>
			</g>
			<defs>
				<mask id={githubMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width="40" height="23">
					<rect width="40" height="23" fill="white" />
					<rect x="16.5" width="23" height="23" rx="6.5" fill="black" />
				</mask>
				<clipPath id={clipId}>
					<rect width="20" height="20" fill="white" transform="translate(18 1.5)" />
				</clipPath>
			</defs>
		</svg>
	);
};

export const DevDocsScmProviderPanelIcons = (): React.JSX.Element => (
	<svg
		width="72"
		height="40"
		viewBox="0 0 72 40"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
		focusable="false"
	>
		<rect
			y="8.28125"
			width="32"
			height="32"
			rx="8"
			transform="rotate(-15 0 8.28125)"
			fill="#F0F1F2"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M17.1113 10.32C11.9907 11.6921 8.97894 17.0613 10.3859 22.3123C11.5115 26.5131 15.1235 29.365 19.1447 29.6349C19.6318 29.5988 19.7231 29.2589 19.6558 29.0078C19.595 28.7811 19.3857 28.0344 19.1689 27.2411C16.7432 28.5074 15.7442 26.9564 15.7442 26.9564C15.0279 25.9705 14.3418 25.8411 14.3418 25.8411C13.3424 25.4764 14.2504 25.2461 14.2504 25.2461C15.1995 25.0637 15.9344 25.845 15.9344 25.845C17.1509 27.0769 18.3805 26.297 18.8446 25.9123C18.7632 25.2755 18.8911 24.7917 19.0927 24.4832C16.9686 24.7947 14.585 24.5596 13.6089 20.9165C13.3307 19.8785 13.4657 18.9333 13.8805 18.1085C13.72 17.8949 13.1435 17.0129 13.2961 15.5684C13.2961 15.5684 14.0063 15.1042 16.108 15.8595C16.7912 15.4506 17.5562 15.1324 18.3433 14.9176C19.1323 14.7101 19.9545 14.603 20.752 14.6151C22.1923 12.9108 23.0384 12.958 23.0384 12.958C23.894 14.1323 23.8356 15.1844 23.8035 15.4497C24.5764 15.9562 25.1635 16.7079 25.4416 17.746C26.4201 21.3977 24.4662 22.783 22.4644 23.5717C22.8761 23.7776 23.3275 24.2768 23.5652 25.1638C23.9061 26.436 24.1696 27.4627 24.2536 27.7758C24.3213 28.0288 24.5677 28.2806 25.0127 28.0613C28.3579 25.816 30.0569 21.5423 28.9317 17.343C27.5247 12.092 22.2325 8.94781 17.1113 10.32Z"
			fill="#161514"
		/>
		<rect
			x="40.2822"
			width="32"
			height="32"
			rx="8"
			transform="rotate(15 40.2822 0)"
			fill="#F0F1F2"
		/>
		<g clipPath="url(#devDocsScmProviderPanelIconsClip0)">
			<g clipPath="url(#devDocsScmProviderPanelIconsClip1)">
				<path
					d="M38.2117 7.72741C39.3552 3.45968 43.7419 0.927018 48.0096 2.07055L63.4644 6.21166C67.7322 7.35519 70.2648 11.7419 69.1213 16.0096L64.9802 31.4644C63.8367 35.7322 59.45 38.2648 55.1822 37.1213L39.7274 32.9802C35.4597 31.8366 32.927 27.4499 34.0706 23.1822L38.2117 7.72741Z"
					fill="#94C748"
				/>
				<path
					d="M59.2023 21.3098L55.8535 28.7278C55.6449 29.175 55.2396 29.3623 54.7702 29.2366L44.0024 26.3513C43.5331 26.2256 43.2757 25.8607 43.3187 25.3691L44.7887 10.9375C44.8316 10.4459 45.1615 10.2088 45.6032 10.3271L61.396 14.5588C61.8377 14.6772 62.0048 15.0475 61.7962 15.4946L60.2123 18.9763C59.9889 19.4787 59.626 19.6182 59.129 19.4851L49.6313 16.9402C49.4933 16.9032 49.3882 16.9638 49.3715 17.1369L48.8889 21.9198C48.8869 22.0377 48.9475 22.1427 49.0579 22.1723L52.4815 23.0896C52.592 23.1192 52.697 23.0586 52.7542 22.9555L54.1665 19.7829C54.3327 19.3836 54.6532 19.2919 55.0398 19.3955L58.7671 20.3942C59.3193 20.5422 59.4109 20.8627 59.2023 21.3098Z"
					fill="#101214"
				/>
			</g>
		</g>
		{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Figma uses mix-blend-multiply for this SVG overlap. */}
		<g style={{ mixBlendMode: 'multiply' }}>
			<path
				d="M37.2309 23.291C37.9215 25.8689 37.2686 28.4885 35.7045 30.4121C34.1412 28.4886 33.4896 25.8693 34.1801 23.292L35.7045 17.5977L37.2309 23.291Z"
				fill="#B7B9BE"
			/>
		</g>
		<defs>
			<clipPath id="devDocsScmProviderPanelIconsClip0">
				<rect width="32" height="32" fill="white" transform="translate(40.2822) rotate(15)" />
			</clipPath>
			<clipPath id="devDocsScmProviderPanelIconsClip1">
				<path
					d="M38.2117 7.72741C39.3552 3.45968 43.7419 0.927018 48.0096 2.07055L63.4644 6.21166C67.7322 7.35519 70.2648 11.7419 69.1213 16.0096L64.9802 31.4644C63.8367 35.7322 59.45 38.2648 55.1822 37.1213L39.7274 32.9802C35.4597 31.8366 32.927 27.4499 34.0706 23.1822L38.2117 7.72741Z"
					fill="white"
				/>
			</clipPath>
		</defs>
	</svg>
);
